import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next/src';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { format } from '@hobs/utils/formatWithTimeZone';
import Dialog from '@hobs/components/Dialog';
import InputLabel from '@hobs/components/InputLabel';
import Toast from '@hobs/components/Toast';
import Confirm from '@hobs/components/Confirm';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import { DATE_FORMAT } from '@hobs/utils/constants';
import Switch from '@hobs/components/Switch';
import DropZone from '@hobs/components/DropZone';
import {
  getInvoice,
  clearInvoices,
  deleteInvoice,
  updateInvoice,
  toggleAsPaid
} from '@hobs/actions/invoices';

import './InvoiceDialog.scss';
import {
  setAttachments,
  uploadAttachments,
  clearAttachments,
  deleteAttachment
} from '@hobs/actions/attachment';
import Money from '@hobs/components/Money';

const defaultState = {
  error: null,
  isPaid: null,
  isPaidDisabled: false
};

class InvoiceDialog extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = defaultState;
  }

  componentDidMount() {
    const { invoice, setAttachments } = this.props;
    if (invoice) {
      this.setState({
        isPaid: invoice.isPaid
      });
      setAttachments(invoice.attachments);
    }
  }

  componentDidUpdate(prevProps) {
    const { invoice, setAttachments } = this.props;

    if (invoice && prevProps.invoice !== invoice) {
      this.setState({
        isPaid: invoice.isPaid
      });
      setAttachments(invoice.attachments);
    }
  }

  onDialogOpening = () => {
    const { getInvoice, match } = this.props;
    getInvoice(match.params.guid);
  };

  onDialogClose = () => {
    const { closeDialog, reload, clearAttachments } = this.props;
    reload();
    closeDialog();
    clearAttachments();
  };

  onDialogClosed = () => {
    const { clearInvoices } = this.props;
    clearInvoices();
    this.resetState();
  };

  setError(message) {
    this.setState({ error: message });
  }

  handleFileUpload = value => {
    if (value) {
      this.props.uploadAttachments({ attachments: value });
    }
  };

  setIsPaid = isPaid => {
    const { invoice, toggleAsPaid, t, getInvoice } = this.props;

    const { invoiceReferenceId } = invoice;
    const markedAs = isPaid ? t('paid') : t('not paid');

    this.setState({
      isPaidDisabled: true,
      isPaid
    });

    const body = {
      invoiceReferenceId,
      isPaid
    };

    toggleAsPaid(body).then(response => {
      if (response.success) {
        getInvoice(invoiceReferenceId)
          .then(response => {
            if (response.success) {
              Toast.success(`${t('Invoice reference marked as')} ${markedAs}`);
            } else {
              Toast.error(t(response.payload.message));
            }

            this.setState(({ isPaidDisabled }) => ({
              isPaidDisabled: !isPaidDisabled
            }));
          })
          .catch(error => {
            Toast.error(t(error.message));

            this.setState(({ isPaidDisabled }) => ({
              isPaidDisabled: !isPaidDisabled
            }));
          });
      } else {
        Toast.error(t(response.payload.message));
        this.setState(({ isPaidDisabled }) => ({
          isPaidDisabled: !isPaidDisabled,
          isPaid: invoice.isPaid
        }));
      }
    });
  };

  resetState() {
    this.setState(defaultState);
  }

  deleteInvoice = () => {
    const { invoice, deleteInvoice, t } = this.props;
    Confirm(
      t('Delete invoice reference'),
      t('Are you sure you want to delete this invoice reference?'),
      () => {
        deleteInvoice(invoice.invoiceReferenceId).then(response => {
          if (response && response.success === true) {
            Toast.success(t('Invoice reference successfully deleted'));
            this.onDialogClose();
          } else if (response.payload && response.payload.message) {
            Toast.warning(response.payload.message);
          } else {
            Toast.warning(t('Cannot delete invoice reference'));
          }
        });
      }
    );
  };

  updateInvoice = () => {
    const { invoice, updateInvoice, attachments, t } = this.props;
    const body = {
      invoiceReferenceId: invoice.invoiceReferenceId,
      attachmentsIds: attachments.map(attachment => attachment.attachmentId)
    };

    updateInvoice(body).then(response => {
      if (response && response.success === true) {
        Toast.success(t('Invoice reference successfully updated'));
        this.onDialogClose();
      } else if (response.payload && response.payload.message) {
        Toast.warning(response.payload.message);
      } else {
        Toast.warning(t('Cannot update invoice reference'));
      }
    });
  };

  renderExerciseCurrency() {
    const { currentExercise } = this.props;

    if (!currentExercise.currency.name) {
      return;
    }

    return ` (${this.props.currentExercise.currency.name})`;
  }

  render() {
    const { t, isDialogOpen, invoice, attachments } = this.props;

    return (
      <Dialog
        title={t('Invoice reference')}
        isOpen={isDialogOpen}
        onClose={this.onDialogClose}
        onClosed={this.onDialogClosed}
        onOpening={this.onDialogOpening}
        className="invoice"
      >
        <div>
          {invoice && (
            <>
              <div className="invoice--row">
                <div className="invoice--column">
                  <InputLabel>{t('Invoice reference No')}</InputLabel>
                  <div className="invoice--info">
                    {invoice.invoiceReferenceNumber}
                  </div>
                </div>
                <div className="invoice--column">
                  <InputLabel>{t('Invoice reference date')}</InputLabel>
                  <div className="invoice--info">
                    {format(invoice.invoiceReferenceDate, DATE_FORMAT)}
                  </div>
                </div>
              </div>
              <div className="invoice--row">
                <div className="invoice--column">
                  <InputLabel>{t('Receiver')}</InputLabel>
                  <div className="invoice--info">{invoice.receiver}</div>
                </div>
                <div className="invoice--column">
                  <InputLabel>{t('Invoiced service lines')}</InputLabel>
                  <div className="invoice--info">
                    {invoice.serviceRequestCount}
                  </div>
                </div>
              </div>
              <div className="invoice--row">
                <div className="invoice--column">
                  <InputLabel>
                    {t('Total actual cost')}
                    {this.renderExerciseCurrency()}
                  </InputLabel>
                  <div className="invoice--info">
                    <Money amount={invoice.totalCost} />
                  </div>
                </div>
                <div className="invoice--column">
                  <InputLabel>
                    {t('Total actual gross amount')}
                    {this.renderExerciseCurrency()}
                  </InputLabel>
                  <div className="invoice--info">
                    <Money amount={invoice.totalGrossAmount} />
                  </div>
                </div>
              </div>
              <div className="invoice--row">
                {this.props.withIsPaidSwitch && (
                  <div className="invoice--column">
                    <InputLabel>{t('Is paid')}</InputLabel>
                    <div>
                      <Switch
                        disabled={
                          this.state.isPaidDisabled || this.props.preview
                        }
                        checked={this.state.isPaid}
                        onChange={this.setIsPaid}
                      />
                    </div>
                  </div>
                )}
                <div className="invoice--column">
                  <InputLabel>{t('Attachments')}</InputLabel>
                  <DropZone
                    disabled={this.props.preview}
                    multiple={true}
                    value={attachments}
                    onSelect={files => this.handleFileUpload(files)}
                    onRemove={file => this.props.deleteAttachment(file)}
                  />
                </div>
              </div>
              <div className="invoice--row">
                <div className="invoice--column">
                  <div className="invoice--buttons">
                    <GhostButton
                      disabled={this.props.preview}
                      danger
                      onClick={this.deleteInvoice}
                    >
                      {t('Delete invoice ref.')}
                    </GhostButton>
                  </div>
                </div>
                <div className="invoice--column">
                  <div className="invoice--buttons">
                    <RaisedButton
                      disabled={this.props.preview || invoice.canHostUnitEdit}
                      type="button"
                      onClick={this.updateInvoice}
                    >
                      {t('Update')}
                    </RaisedButton>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Dialog>
    );
  }
}

InvoiceDialog.defaultProps = {
  withIsPaidSwitch: true
};

InvoiceDialog.propTypes = {
  t: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  isDialogOpen: PropTypes.bool,
  closeDialog: PropTypes.func,
  reload: PropTypes.func,
  clearInvoices: PropTypes.func,
  invoice: PropTypes.object,
  getInvoice: PropTypes.func,
  deleteInvoice: PropTypes.func,
  updateInvoice: PropTypes.func,
  toggleAsPaid: PropTypes.func,
  uploadAttachments: PropTypes.func,
  clearAttachments: PropTypes.func,
  deleteAttachment: PropTypes.func,
  setAttachments: PropTypes.func,
  withIsPaidSwitch: PropTypes.bool,
  preview: PropTypes.bool,
  currentExercise: PropTypes.object
};

const mapStateToProps = state => ({
  invoice: state.invoices.invoices,
  attachments: state.attachment.attachments,
  currentExercise: state.exercise.current
});

const mapDispatchToProps = {
  getInvoice,
  clearInvoices,
  deleteInvoice,
  updateInvoice,
  toggleAsPaid,
  uploadAttachments,
  clearAttachments,
  deleteAttachment,
  setAttachments
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withTranslation()(InvoiceDialog))
);
